import React, {  useEffect, useState } from "react";
import ReactGA from "react-ga4";
import { useParams } from "react-router-dom";
import "../../style/Hostage.css";
import {
  Box,
  Button,
  Container,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import LetterForm from "../Letters/LetterForm";
import UnsupportHostageDrawer from "./UnsupportHostageDrawer";
import { Hostage, Story, User, Letter } from "../types";
import NoActivitiesSVG from "../../icons/no-activities.svg";
import { decode } from "../../utils/Utils";
import LetterList from "./LetterList";
import { fetchHostageData, fetchHostageLetters } from "./hostageFetchUtils";

const HostageLetters = () => {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const [hostage, setHostage] = useState<Hostage | null>(null);
  const [isAddLetterDrawerOpen, setIsAddLetterDrawerOpen] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [openConfirmDrawer, setOpenConfirmDrawer] = useState(false);
  const [letters, setLetters] = useState<Letter[]>([]);

  const navigate = useNavigate();

  useEffect(() => {
    if (!id) {
      console.log("No id found, redirecting to home");
      navigate("/");
      return;
    }

    // @ts-ignore
    fetchHostageData(id)
      .then(({ hostageData, userData }) => {
        setHostage(hostageData);
        setUser(userData);

        // Fetch letters after setting hostage data
        return fetchHostageLetters(hostageData.id);
      })
      .then(lettersData => {
        console.log ('setting letters', lettersData)
        setLetters(lettersData);
      })
      .catch(() => {
        console.log(`No data found for id: ${id}, redirecting to home`);
        navigate("/"); // Redirect to home if no data is found
      });
  }, []);

  if (!hostage) {
    return <div>{t("hostageCard.loading")}</div>; // Show a loading message while fetching data
  }

  if (!user) {
    return null;
  }

  function getHostageLetters(){
    fetchHostageLetters(hostage?.id).then((data) => setLetters(data));
  }


  const isJoined = user.supported_hostages.includes(hostage.id);
  const ENVIRONMENT = process.env.NODE_ENV || "development";
  const SERVER_BASE_URL = ENVIRONMENT === "development" ? "http://localhost:3001" : "";

  const handleConfirmUnsupport = async () => {
    try {
      const response = await fetch(`${SERVER_BASE_URL}/api/user/unsupport`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ hostageId: id }),
      });
      const res = await response.json();
      console.log("unSupport request successful:", res);

      ReactGA.event({
        category: "Event",
        action: "unSupport Hostage Clicked",
        label: id,
      });
      setOpenConfirmDrawer(false); // Close the drawer after success
      window.location.reload();
    } catch (err) {
      console.error("Error sending unSupport request:", err);
    }
  };

  const handleUnsupportClick = () => {
    setOpenConfirmDrawer(true); // Open the confirmation dialog
  };



  const handleCreateLetter = () => {
    setIsAddLetterDrawerOpen(true);
  };

  const handleDeleteLetter = (letterId?: string) => {
    if (letterId) {
      setLetters(prevLetter => prevLetter.filter(letter => letter.id !== letterId));
    }
  };

  function isAdmin() {
    return !!(
      user?.role === "admin" ||
      (user?.role === "moderator" && hostage && user?.moderating?.includes(hostage.id))
    );
  }


  return (
    <Container maxWidth="sm" className="hostage-card-container" >
      
      <Button 
        variant="contained"
        color="primary"
        sx={{
          padding: "12px 24px",
          marginTop: "10px",
          textTransform: "none",
          backgroundColor: "#FF395D",
          display: "block",
          top: "5px",
          margin: "0 auto", 
          "&:hover": {
            backgroundColor: "#e03551",
          },
        }}onClick={handleCreateLetter}>{t("createLetter.title")}
      </Button>
     
      <LetterForm
        open={isAddLetterDrawerOpen}
        onClose={() => {
          setIsAddLetterDrawerOpen(false);
          getHostageLetters();
        }}
        hostageId={hostage.id}
        isAdmin={isAdmin()}
      />
      {letters.length === 0 ? (
        <>
          <Typography variant="body1" sx={{ textAlign: "center", fontWeight: 600, margin: "8px 4px 10px" }}>
            {decode(t("userLetters.emptyTitle", { name: hostage[i18n.language as "en" | "he"].first_name }))}
          </Typography>
          <Typography variant="body2" sx={{ textAlign: "center", margin: "0px 20px 20px 20px" }}>
            {decode(t("userLetters.emptyHostageLettersSubtitle", { name: hostage[i18n.language as "en" | "he"].first_name }))}
          </Typography>
        </>
      ) : (
        <>
          <LetterList letters={letters} hostage={hostage} onDeleteLetter={handleDeleteLetter} />
        </>
      )}
      <LetterForm
        open={isAddLetterDrawerOpen}
        onClose={() => {
          setIsAddLetterDrawerOpen(false);
          getHostageLetters();
        }}
        hostageId={hostage.id}
        isAdmin={isAdmin()}
      />
    </Container>
  );
};

export default HostageLetters;
